import { useMutation, useQueryClient } from '@tanstack/react-query';
import { db } from '@util/firebase';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { collection, doc, updateDoc, getDoc } from 'firebase/firestore';
import type { SupportState, ChatDocument } from 'models/chat';

const chatsRef = collection(db, 'messages');

export const useConversationMutations = () => {
  const queryClient = useQueryClient();
  const { userDoc } = useAuth();
  const { showSuccessToast, showErrorToast } = useToastContext();

  const updateSupportState = useMutation({
    mutationFn: async ({
      chat_id,
      state,
    }: {
      chat_id: string;
      state: SupportState;
    }) => {
      if (!chat_id) throw new Error('No chat id provided');
      const ref = doc(chatsRef, chat_id);
      await updateDoc(ref, {
        support_state: state,
      });
      return chat_id;
    },
    onError: (error) => {
      showErrorToast((error as Error).message);
    },
    onSuccess: (chat_id) => {
      queryClient.invalidateQueries({
        queryKey: ['chat', chat_id],
      });
      queryClient.invalidateQueries({
        queryKey: ['chats'],
      });
      showSuccessToast('Support ticket updated');
    },
  });

  const assignTicket = useMutation({
    mutationFn: async ({ chat_id, uid }: { chat_id: string; uid: string }) => {
      if (!chat_id) throw new Error('No chat id provided');
      const ref = doc(chatsRef, chat_id);
      await updateDoc(ref, {
        support_assigned: uid,
      });
      return chat_id;
    },
    onError: (error) => {
      showErrorToast((error as Error).message);
    },
    onSuccess: (chat_id) => {
      queryClient.invalidateQueries({
        queryKey: ['chat', chat_id],
      });
      queryClient.invalidateQueries({
        queryKey: ['chats'],
      });
      showSuccessToast('Ticket assigned');
    },
  });

  const markAsRead = useMutation({
    mutationFn: async (chat_id: string) => {
      if (!chat_id) throw new Error('No chat id provided');
      if (!userDoc?.uid) throw new Error('No user uid provided');
      const ref = doc(chatsRef, chat_id);
      await updateDoc(ref, {
        [`unread.${userDoc.uid}`]: false,
      });
      return chat_id;
    },
    onMutate: async (chat_id: string) => {
      if (!userDoc?.uid) return;

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['chats'] });
      await queryClient.cancelQueries({ queryKey: ['chat', chat_id] });

      // Snapshot the previous value
      const previousChats = queryClient.getQueryData(['chats']) as
        | ChatDocument[]
        | undefined;

      // Optimistically update the chat to mark as read
      const newChats = previousChats?.map((chat) => {
        if (chat.id === chat_id) {
          return {
            ...chat,
            unread: {
              ...chat.unread,
              [userDoc.uid]: false,
            },
          };
        }
        return chat;
      });

      // Optimistically update to the new value
      if (newChats) {
        queryClient.setQueryData(['chats'], newChats);
      }

      // Return a context object with the snapshotted value
      return { previousChats };
    },
    onError: (error, _chat_id, context) => {
      // If the mutation fails, use the context we returned
      if (context?.previousChats) {
        queryClient.setQueryData(['chats'], context.previousChats);
      }
      showErrorToast((error as Error).message);
    },
    onSuccess: (chat_id) => {
      queryClient.invalidateQueries({
        queryKey: ['chat', chat_id],
      });
      queryClient.invalidateQueries({
        queryKey: ['chats'],
      });
    },
  });

  const markAsUnread = useMutation({
    mutationFn: async (chat_id: string) => {
      if (!chat_id) throw new Error('No chat id provided');
      if (!userDoc?.uid) throw new Error('No user uid provided');
      const ref = doc(chatsRef, chat_id);

      await updateDoc(ref, {
        [`unread.${userDoc.uid}`]: true,
      });
      return chat_id;
    },
    onMutate: async (chat_id: string) => {
      if (!userDoc?.uid) return;

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['chats'] });
      await queryClient.cancelQueries({ queryKey: ['chat', chat_id] });

      // Snapshot the previous value
      const previousChats = queryClient.getQueryData(['chats']) as
        | ChatDocument[]
        | undefined;

      // Optimistically update the chat to mark as unread
      const newChats = previousChats?.map((chat) => {
        if (chat.id === chat_id) {
          return {
            ...chat,
            unread: {
              ...chat.unread,
              [userDoc.uid]: true,
            },
          };
        }
        return chat;
      });

      // Optimistically update to the new value
      if (newChats) {
        queryClient.setQueryData(['chats'], newChats);
      }

      // Return a context object with the snapshotted value
      return { previousChats };
    },
    onError: (error, _chat_id, context) => {
      // If the mutation fails, use the context we returned
      if (context?.previousChats) {
        queryClient.setQueryData(['chats'], context.previousChats);
      }
      showErrorToast((error as Error).message);
    },
    onSuccess: (chat_id) => {
      queryClient.invalidateQueries({
        queryKey: ['chat', chat_id],
      });
      queryClient.invalidateQueries({
        queryKey: ['chats'],
      });
      showSuccessToast('Chat marked as unread');
    },
  });

  const archiveChat = useMutation({
    mutationFn: async (chat_id: string) => {
      if (!chat_id) throw new Error('No chat id provided');
      if (!userDoc?.uid) throw new Error('No user uid provided');

      const ref = doc(chatsRef, chat_id);
      const chat = (await getDoc(ref)).data() as ChatDocument;

      await updateDoc(ref, {
        uids: chat.uids?.filter((uid: string) => uid !== userDoc.uid) || [],
      });
      return chat_id;
    },
    onError: (error) => {
      showErrorToast((error as Error).message);
    },
    onSuccess: (chat_id) => {
      queryClient.invalidateQueries({
        queryKey: ['chat', chat_id],
      });
      queryClient.invalidateQueries({
        queryKey: ['chats'],
      });
      showSuccessToast('Chat archived');
    },
  });

  const starChat = useMutation({
    mutationFn: async (chat_id: string) => {
      if (!chat_id) throw new Error('No chat id provided');
      if (!userDoc?.uid) throw new Error('No user uid provided');

      const ref = doc(chatsRef, chat_id);
      const chat = (await getDoc(ref)).data() as ChatDocument;

      const isCurrentlyFavorited = chat.favorited?.includes(userDoc.uid);

      if (isCurrentlyFavorited) {
        const updatedFavorited = chat.favorited?.filter(
          (uid) => uid !== userDoc.uid
        );

        await updateDoc(ref, {
          favorited: updatedFavorited || [],
        });
      } else {
        await updateDoc(ref, {
          favorited: [...(chat.favorited || []), userDoc.uid],
        });
      }

      return chat_id;
    },
    onError: (error) => {
      showErrorToast((error as Error).message);
    },
    onSuccess: (chat_id) => {
      queryClient.invalidateQueries({
        queryKey: ['chat', chat_id],
      });
    },
  });

  return {
    updateSupportState,
    assignTicket,
    markAsRead,
    markAsUnread,
    archiveChat,
    starChat,
  };
};
